import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppContext, useAsyncData } from '@/hooks';
import { api } from '@/api/client';
import type { InstitutionUser, SecurityOverview } from '@/types';
import { Card } from '@/components/ui/Card';
import { Icon } from '@/components/icons';
import styles from './AdminSecurityPage.module.css';

interface SecurityData {
  overview: SecurityOverview;
  users: InstitutionUser[];
}

const PRIVILEGED_ROLE_CODES = new Set([
  'INSTITUTION_ADMIN',
  'INSTITUTION_ADMINISTRATOR',
  'PORTAL_ADMIN',
  'PORTAL_ADMINISTRATOR',
]);

export function AdminSecurityPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';

  const { state, reload } = useAsyncData<SecurityData>(
    `admin-security-${institutionId}`,
    async () => {
      const [overview, users] = await Promise.all([
        api.admin.security.overview(institutionId),
        api.admin.users.list(institutionId),
      ]);
      return { overview, users };
    },
    !!institutionId,
  );

  if (state.status === 'loading') {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>{t('admin.security.loading')}</div>
      </div>
    );
  }

  if (state.status === 'error') {
    const error = state.error;
    return (
      <div className={styles.page}>
        <Card className={styles.emptyState}>
          <Icon name={error.isMissingEndpoint ? 'info' : 'error'} size={48} />
          <h2 className={styles.emptyStateTitle}>{t('admin.security.title')}</h2>
          <p className={styles.emptyStateBody}>
            {t(error.isMissingEndpoint ? 'platform.failure.missing.body' : 'platform.failure.generic.body')}
          </p>
          <button className={styles.retryButton} onClick={reload}>{t('common.retry')}</button>
        </Card>
      </div>
    );
  }

  const { overview, users } = state.data;
  const privilegedUsers = users.filter((u) => PRIVILEGED_ROLE_CODES.has(u.roleCode));
  const usersWithoutMfa = users.filter((u) => !u.mfaEnabled);

  return (
    <div className={styles.page}>
      <div className="page-header">
        <h1>{t('admin.security.title')}</h1>
        <p className="page-subtitle">{t('admin.security.subtitle')}</p>
      </div>

      <div className={styles.metricsGrid}>
        <Card className={styles.metricCard}>
          <div className={styles.metricValue}>{overview.totalUsers}</div>
          <div className={styles.metricLabel}>{t('admin.security.totalUsers')}</div>
        </Card>
        <Card className={styles.metricCard}>
          <div className={styles.metricValue}>{overview.privilegedRoleCount}</div>
          <div className={styles.metricLabel}>{t('admin.security.privilegedRoles')}</div>
        </Card>
        <Card className={styles.metricCard}>
          <div className={styles.metricValue}>{overview.usersWithoutMfa}</div>
          <div className={styles.metricLabel}>{t('admin.security.usersWithoutMfa')}</div>
        </Card>
      </div>

      <div className={styles.contentGrid}>
        <Card className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('admin.security.accessReviewItems')}</h2>
            <button
              type="button"
              className={styles.actionBtn}
              onClick={() => navigate('/admin/institution/roles')}
            >
              {t('admin.security.reviewPrivilegedRoles')}
            </button>
          </div>
          {privilegedUsers.length === 0 ? (
            <p className={styles.emptyStateBody}>{t('admin.security.noReviewItems')}</p>
          ) : (
            <ul className={styles.reviewList}>
              {privilegedUsers.map((user) => (
                <li key={user.id} className={styles.reviewItem}>
                  <div className={styles.reviewInfo}>
                    <span className={styles.reviewUser}>{user.name} ({user.email})</span>
                    <span className={styles.reviewMeta}>{user.roleName}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('admin.security.usersWithoutMfa')}</h2>
          </div>
          {usersWithoutMfa.length === 0 ? (
            <p className={styles.emptyStateBody}>{t('admin.security.noSessions')}</p>
          ) : (
            <ul className={styles.sessionList}>
              {usersWithoutMfa.slice(0, 10).map((user) => (
                <li key={user.id} className={styles.sessionItem}>
                  <div className={styles.sessionInfo}>
                    <span className={styles.sessionUser}>{user.name} ({user.email})</span>
                    <span className={styles.sessionTimes}>{user.roleName}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
